<template>
	<div class="container_wrapper">
		<Swiper class="container" v-bind="mainSwiperOptions" @swiper="onSwiper">
			<SwiperSlide
				v-for="(item, index) in props.data"
				:key="`${index}`"
				:class="['container_item', 'cursor-pointer', { on: findMatchingIndex(selectedPk) === index }]"
				@click="onChangeMaster(item.major_holder_event_pk, index)"
			>
				<div class="container_item_img">
					<img :src="item.image_path" alt="대가 일러스트" loading="lazy" />
				</div>
				<p class="container_item_name">{{ item.major_holder_name }}</p>
			</SwiperSlide>
			<div class="custom-navigation">
				<button class="custom-nav-prev" aria-label="prev-arrow"><IconArrowPrev width="22" height="22" /></button>
				<div class="swiper-pagination"></div>
				<button class="custom-nav-next" aria-label="next-arrow"><IconArrowNext width="22" height="22" /></button>
			</div>
		</Swiper>
	</div>
</template>
<script setup lang="ts">
// core
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
// lib
import { debounce } from 'lodash';
import { FreeMode, Navigation, Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
//swiper
import { Swiper, SwiperSlide } from 'swiper/vue';

//mock
import { MajorHolderNameType } from '@/api/majorHolder';

import IconArrowNext from '../atom/icon/IconArrowNext.vue';
import IconArrowPrev from '../atom/icon/IconArrowPrev.vue';

// -----variable-----
const props = defineProps<{
	data: MajorHolderNameType[];
	selectedPk?: string;
}>();
const emit = defineEmits(['update:major-holder-pk']);

const route = useRoute();
const swiperComponent = ref();
const selectedPk = computed(() => {
	return props.selectedPk;
});
const previousWidth = ref(window.innerWidth);

// -----swiper-----
const mainSwiperOptions: SwiperOptions = {
	modules: [FreeMode, Pagination, Navigation],
	slidesPerView: 'auto',
	freeMode: false,
	observer: true,
	observeParents: true,
	updateOnWindowResize: true,
	pagination: {
		el: '.swiper-pagination',
		type: 'fraction',
		formatFractionCurrent: function (number: number) {
			return ('0' + number).slice(-2);
		},
		formatFractionTotal: function (number: number) {
			return ('0' + number).slice(-2);
		},
		renderFraction: (currentClass: string, totalClass: string) => {
			return (
				'<span class="' +
				currentClass +
				'"></span>' +
				'<span class="fraction-divider">|</span>' +
				'<span class="' +
				totalClass +
				'"></span>'
			);
		},
	},
	navigation: {
		nextEl: '.custom-nav-next',
		prevEl: '.custom-nav-prev',
	},
};

// -----function-----
// 해당 마스터로 슬라이드 이동
const onSwiper = (swiper: SwiperOptions) => {
	swiperComponent.value = swiper;
};

// 대가 변경 시 함수
const onChangeMaster = async (pk: number, idx: number) => {
	if (swiperComponent.value) swiperComponent.value?.slideTo(idx);
	emit('update:major-holder-pk', pk);
};

// 화면 크기 변경 시 리로드 함수 (swiper 작동 오류)
const handleResize = debounce(() => {
	const currentWidth = window.innerWidth;
	if (currentWidth !== previousWidth.value) {
		previousWidth.value = currentWidth;

		// Swiper 인스턴스 업데이트
		if (swiperComponent.value) {
			swiperComponent.value.update();
			swiperComponent.value.updateSlides();
			swiperComponent.value.updateSize();
			swiperComponent.value.updateProgress();

			// 현재 선택된 슬라이드로 이동
			if (route.query.pk) {
				swiperComponent.value.slideTo(findMatchingIndex(String(route.query.pk)));
			}
		}
	}
}, 300); // debounce 시간을 1000ms에서 300ms로 줄임

// 특정 PK를 가지는 항목의 인덱스를 추출하는 함수
const findMatchingIndex = (queryPk?: string) => {
	return props.data.findIndex((item) => String(item.major_holder_event_pk) === String(queryPk));
};

// -----composition-----
onMounted(() => {
	if (swiperComponent.value && route.query.pk) swiperComponent.value.slideTo(findMatchingIndex(String(route.query.pk)));
	window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
	window.removeEventListener('resize', handleResize);
	handleResize.cancel();
});

watch(
	() => route.query.pk,
	(newQueryPk) => {
		if (swiperComponent.value && newQueryPk) swiperComponent.value.slideTo(findMatchingIndex(String(newQueryPk)));
	},
	{
		immediate: true,
	}
);
</script>

<style lang="scss" scoped>
.container {
	.custom-navigation {
		position: absolute;
		bottom: 0;
		right: 0;
		display: flex;
		align-items: center;
		gap: 1rem;
		height: 2.6rem;
		border-radius: 10rem;
		z-index: 10;
		padding: 0.2rem 0.4rem;
		.custom-nav-prev {
			justify-content: flex-end;
		}
		.custom-nav-prev,
		.custom-nav-next {
			height: 100%;
			background: transparent;
			border: none;
			cursor: pointer;
			color: var(--color-base-white);
			display: flex;
			align-items: center;
			&::after {
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
			}
		}

		:deep(.swiper-pagination) {
			position: static;
			width: auto;
			display: flex;
			align-items: center;
			gap: 0.5rem;
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);

			.swiper-pagination-current {
				color: var(--color-base-white);
			}

			.fraction-divider {
				color: var(--color-base-mid);
				margin: 0 0.5rem;
			}

			.swiper-pagination-total {
				color: var(--color-base-light);
			}
		}
	}
}

.container {
	width: 100%;
	height: 17.6rem;
	&_item {
		display: flex;
		flex-direction: column;
		align-items: center;
		// justify-content: center;
		gap: 1rem;
		width: 10.6rem;
		padding: 1.6rem;
		&_img {
			width: 7.4rem;
			height: 7.4rem;
			border-radius: var(--border-radius-full);
			background-color: #d9d9d9;
			overflow: hidden;
			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}
		&_name {
			font-size: var(--font-s-mid);
			font-weight: var(--font-w-semi-bold);
			color: var(--color-base-white);
			line-height: 1.8rem;
			text-align: center;
		}
	}
	:deep(.swiper-pagination) {
		width: 100%;
		font-weight: var(--font-w-regular);
		font-size: var(--font-s-small);
		color: #fff;
		bottom: 0;
		text-align: end;

		.swiper-pagination-current {
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 1.56rem;
		}

		.swiper-pagination-total {
			color: var(--color-base-light);
			font-size: var(--font-s-small);
			font-weight: var(--font-w-light);
			line-height: 1.56rem;

			.swiper-button-prev {
				position: unset;
				margin-top: 0;
				height: inherit;
				width: 2.2rem;
			}
			.swiper-button-next {
				position: unset;
				margin-top: 0;
				height: inherit;
				width: 2.2rem;
			}
			.swiper-button-prev::after {
				color: var(--color-base-white);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
			}
			.swiper-button-next::after {
				color: var(--color-base-white);
				font-size: var(--font-s-small);
				font-weight: var(--font-w-light);
				line-height: 1.551rem;
			}
		}
	}
	.on {
		.container_item_img {
			border: 1px solid var(--color-primary);
		}
		.container_item_name {
			color: var(--color-primary);
		}
	}
}
</style>
